import React, { useState } from 'react';
import { Box, Stack } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import ByzMuiSelect from 'src/components/core-components/mui/ByzMuiSelect';
import ByzMuiTextField from 'src/components/core-components/mui/inputs/ByzMuiTextField';
import ByzMuiSaveButton from 'src/components/core-components/mui/buttons/ByzMuiSaveButton';
import ByzMuiTypography from 'src/components/core-components/mui/inputs/ByzMuiTypography';
import { useBaseMutationWithVariables, useBaseQuery } from 'src/api/core/useBaseQuery';
import { queryClient } from 'src';

interface LiquidInModalProps {
  comId: number;
  ptId: number;
  onClose: () => void;
  onSave?: (data: any) => void;
}

const LiquidInModal: React.FC<LiquidInModalProps> = ({ comId, ptId, onClose, onSave }) => {

  
  const [formData, setFormData] = useState({
    typeId: '',
    nameId: '',
    amount: '',
    duration: '1',
    speed: '',
  });



  const { mutate: createFluidIntake } = useBaseMutationWithVariables({
    endpoint: `/reference/api/Card/CreateFluidBalanceFluidIntake`,
    method: 'POST',
  });

  const { data: fluidTypes, isLoading: typesLoading } = useBaseQuery<any>({
    endpoint: `/reference/api/Card/GetMcFluidBalanceFluidTypes`,
    queryKey: ['fluidTypes'],
    options: {
      enabled: true,
    }
  });


  const { data: fluidNames, isLoading: namesLoading } = useBaseQuery<any>({
    endpoint: `/reference/api/Card/GetMcFluidBalanceFluidNames?ftId=${formData.typeId}`,
    queryKey: ['fluidNames', formData.typeId],
    options: {
      enabled: !!formData.typeId,
    }
  });

  const sanitizePositiveNumber = (value: string): string => {
    const cleaned = value.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    const sanitized = parts.length > 1 ? parts[0] + '.' + parts.slice(1).join('') : cleaned;
    const num = parseFloat(sanitized);
    if (isNaN(num) || num < 0) return '';
    return sanitized;
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: field === 'typeId' || field === 'nameId' ? value : sanitizePositiveNumber(value)
      };
      
      if (field === 'typeId') {
        newData.nameId = '';
      }

      if (field === 'amount' || field === 'duration') {
        const amount = field === 'amount' ? parseFloat(value) : parseFloat(prev.amount);
        const duration = field === 'duration' ? parseFloat(value) : parseFloat(prev.duration);
        
        if (!isNaN(amount) && !isNaN(duration) && duration > 0) {
          newData.speed = Math.round(amount / duration).toString();
        }
      }

      if (field === 'speed') {
        const amount = parseFloat(prev.amount);
        const speed = parseFloat(value);
        
        if (!isNaN(amount) && !isNaN(speed) && speed > 0) {
          newData.duration = Math.round(amount / speed).toString();
        }
      }
      
      return newData;
    });
  };

  const toLocalISOString = (date: Date): string => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const handleSave = () => {
    const payload = {
      PtId: ptId,
      FluidId: Number(formData.nameId),
      FluidTypeId: Number(formData.typeId),
      StartingAmount: Number(formData.amount),
      AppliedAmount: 0,
      Ptime: toLocalISOString(new Date()),
      DeliverySpeedPlan: Number(formData.speed),
    };





    createFluidIntake(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['LiquidInTable'] });
        queryClient.invalidateQueries({ queryKey: ['fluidTypesBalance'] });
        onClose();
      },
      onError: (error) => {
        console.error('Sıvı girişi kaydedilemedi:', error);
      },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2}>
        <Grid xs={12} container alignItems="center" justifyContent="space-between">
          <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
            <ByzMuiTypography sx={{ marginLeft: "30px" }}>
              Tipi
            </ByzMuiTypography>
          </Grid>
          <Grid xs={12} sm={6} md={6}>
            <ByzMuiSelect
              label="Tipi"
              value={
                formData.typeId && fluidTypes?.data
                  ? { 
                      id: formData.typeId, 
                      value: formData.typeId,
                      label: fluidTypes.data.find((t: any) => t.id === formData.typeId)?.name || ''
                    }
                  : null
              }
              onChange={(event: any, newValue: any) => handleChange('typeId', newValue?.id || '')}
              options={fluidTypes?.data?.map((type: any) => ({
                value: type.id,
                label: type.name,
                id: type.id
              })) || []}
              isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              disabled={typesLoading}
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>

        <Grid xs={12} container alignItems="center" justifyContent="space-between">
          <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
            <ByzMuiTypography sx={{ marginLeft: "30px" }}>
              Adı
            </ByzMuiTypography>
          </Grid>
          <Grid xs={12} sm={6} md={6}>
            <ByzMuiSelect
              label="Adı"
              value={
                formData.nameId && fluidNames?.data
                  ? { 
                      fid: formData.nameId,
                      value: formData.nameId,
                      label: fluidNames.data.find((n: any) => n.id === formData.nameId)?.name || ''
                    }
                  : null
              }
              onChange={(event: any, newValue: any) => handleChange('nameId', newValue?.fid || '')}
              options={fluidNames?.data?.map((name: any) => ({
                value: name.id,
                label: name.name,
                fid: name.id
              })) || []}
              isOptionEqualToValue={(option: any, value: any) => option.fid === value.fid}
              disabled={!formData.typeId || namesLoading}
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>

        <Grid xs={12} container alignItems="center" justifyContent="space-between">
          <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
            <ByzMuiTypography sx={{ marginLeft: "30px" }}>
              Miktarı (ml)
            </ByzMuiTypography>
          </Grid>
          <Grid xs={12} sm={6} md={6}>
            <ByzMuiTextField
              label="Miktarı (ml)"
              type="number"
              value={formData.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>

        <Grid xs={12} container alignItems="center" justifyContent="space-between">
          <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
            <ByzMuiTypography sx={{ marginLeft: "30px" }}>
              Veriliş Süresi
            </ByzMuiTypography>
          </Grid>
          <Grid xs={12} sm={6} md={6}>
            <ByzMuiTextField
              label="Veriliş Süresi (saat)"
              type="number"
              value={formData.duration}
              onChange={(e) => handleChange('duration', e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>


        <Grid xs={12} container alignItems="center" justifyContent="space-between">
          <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
            <ByzMuiTypography sx={{ marginLeft: "30px" }}>
              Hız (ml/sa)
            </ByzMuiTypography>
          </Grid>
          <Grid xs={12} sm={6} md={6}>
            <ByzMuiTextField
              label="Hız (ml/sa)"
              type="number"
              value={formData.speed}
              onChange={(e) => handleChange('speed', e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>

        <Grid xs={12}>
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
            <ByzMuiSaveButton onClick={handleSave} />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LiquidInModal;
